/**
 * What a lapsed account is still allowed to reach, in the experiment arm.
 *
 * Two lists, because "shown in the sidebar" and "allowed to render" are not the same thing:
 *
 * - `LAPSED_SETTINGS_NAV_PATHS` — the trimmed Manage Account sidebar. Everything else is
 *   hidden and hard-gated in `useSettingsNav` (SettingsLayout.tsx).
 * - `LAPSED_ALLOWED_PATHS` — routes the paywall popup does NOT cover, so they are usable.
 *   A superset: it adds payment-return routes that are never linked but must render, or a
 *   3DS redirect back from Stripe would land behind the paywall with the charge in limbo.
 *
 * Sub-Account Billing is deliberately absent: on current flags it is a scio1 iframe, which
 * would render scio1's own inactive-account modal inside the page.
 */

/** Sidebar entries, in display order. */
export const LAPSED_SETTINGS_NAV_PATHS: readonly string[] = [
  '/app/s/personal-profile',
  '/app/s/company-profile',
  '/app/s/account-members',
  '/app/s/subscription-plan',
  '/app/s/payment',
  '/app/s/payment-history',
];

/** Where "Manage Account" lands — the most actionable page for someone who needs to pay. */
export const LAPSED_MANAGE_ACCOUNT_PATH = '/app/s/subscription-plan';

/**
 * Fallback landing page. Subscription Plan is gated on `isRootAdmin` (and hidden entirely by
 * the white-label `hideSubscriptionPages` flag), so sending a non-admin there would trip
 * SettingsLayout's access check and bounce them to a screen the paywall covers — a dead end.
 * Personal Profile is ungated, so everyone has somewhere to land.
 */
export const LAPSED_FALLBACK_ACCOUNT_PATH = '/app/s/personal-profile';

/** Renders without the paywall over it. Superset of the nav list. */
export const LAPSED_ALLOWED_PATHS: readonly string[] = [
  ...LAPSED_SETTINGS_NAV_PATHS,
  // Stripe redirect targets — unlinked, but must render to finish a payment.
  '/app/s/payment-intent',
  '/app/s/confirm-linked-account',
];

/** True when this path is usable by a lapsed account (exact match or a sub-path). */
export function isLapsedAllowedPath(pathname: string): boolean {
  return LAPSED_ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
